import { useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { ExtrudeGeometry, Group, Shape } from "three";

/** Registro módulo-nivel de ventanas montadas (lo consume WindowInstances). */
export interface WindowInstanceSpec {
  el: Group;
  width: number;
  height: number;
}
export const WINDOW_REGISTRY: Set<WindowInstanceSpec> = new Set();

/** Marco perimetral + paño central como ExtrudeGeometry con dos huecos. */
export function buildWindowFrameGeometry(width: number, height: number, t = 0.06): ExtrudeGeometry {
  const outer = new Shape();
  outer.moveTo(-width / 2 - t, -height / 2 - t);
  outer.lineTo(width / 2 + t, -height / 2 - t);
  outer.lineTo(width / 2 + t, height / 2 + t);
  outer.lineTo(-width / 2 - t, height / 2 + t);
  outer.closePath();
  for (const sy of [1, -1] as const) {
    const hole = new Shape();
    const y0 = sy === 1 ? 0.02 : -height / 2;
    const y1 = sy === 1 ? height / 2 : -0.02;
    hole.moveTo(-width / 2, y0);
    hole.lineTo(width / 2, y0);
    hole.lineTo(width / 2, y1);
    hole.lineTo(-width / 2, y1);
    hole.closePath();
    outer.holes.push(hole);
  }
  const geo = new ExtrudeGeometry(outer, { depth: 0.1, bevelEnabled: false });
  geo.translate(0, 0, -0.05);
  return geo;
}

export interface Opening {
  /** Distancia desde el inicio del muro (m). */
  x: number;
  width: number;
  height: number;
  /** Altura del antepecho; 0 para puertas. */
  sill?: number;
}

interface WallProps {
  length: number;
  height: number;
  thickness?: number;
  openings?: Opening[];
  position?: [number, number, number];
  /** Rotación alrededor de Y (rad). El muro se construye a lo largo de +X. */
  rotationY?: number;
  material?: import("three").Material;
  color?: string;
  roughness?: number;
  castShadow?: boolean;
  receiveShadow?: boolean;
  children?: ReactNode;
}

/**
 * Muro procedural con vanos reales (puertas/ventanas) construido como
 * Shape con huecos extruido: la abertura atraviesa el muro de verdad.
 */
export function Wall({
  length,
  height,
  thickness = 0.2,
  openings = [],
  position = [0, 0, 0],
  rotationY = 0,
  material,
  color = "#d8d4c8",
  roughness = 0.9,
  castShadow = true,
  receiveShadow = true,
  children,
}: WallProps) {
  const geometry = useMemo(() => {
    const shape = new Shape();
    shape.moveTo(0, 0);
    shape.lineTo(length, 0);
    shape.lineTo(length, height);
    shape.lineTo(0, height);
    shape.closePath();

    for (const o of openings) {
      const sill = o.sill ?? 0;
      const hole = new Shape();
      hole.moveTo(o.x, sill);
      hole.lineTo(o.x + o.width, sill);
      hole.lineTo(o.x + o.width, sill + o.height);
      hole.lineTo(o.x, sill + o.height);
      hole.closePath();
      shape.holes.push(hole);
    }

    const geo = new ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
    // Centrar en X y apoyar el espesor sobre el eje del muro.
    geo.translate(-length / 2, 0, -thickness / 2);
    return geo;
  }, [length, height, thickness, openings]);

  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh geometry={geometry} material={material} castShadow={castShadow} receiveShadow={receiveShadow}>
        {!material && <meshStandardMaterial color={color} roughness={roughness} />}
      </mesh>
      {children}
    </group>
  );
}

/**
 * Ventana: solo registra posición/tamaño; la geometría real la dibuja
 * <WindowInstances/> del edificio con instancedMesh (vidrio, marcos por
 * variante de tamaño y antepechos), reduciendo ~90 meshes a ~6 llamadas.
 */
export function WindowPane({
  width,
  height,
  position,
  rotationY = 0,
}: {
  width: number;
  height: number;
  position: [number, number, number];
  rotationY?: number;
}) {
  const ref = useRef<Group>(null);
  useLayoutEffect(() => {
    const spec: WindowInstanceSpec = { el: ref.current!, width, height };
    WINDOW_REGISTRY.add(spec);
    return () => {
      WINDOW_REGISTRY.delete(spec);
    };
  }, [width, height]);
  return <group ref={ref} position={position} rotation={[0, rotationY, 0]} />;
}

/** Hoja de puerta doble institucional. */
export function DoorLeaf({
  width,
  height,
  position,
  rotationY = 0,
}: {
  width: number;
  height: number;
  position: [number, number, number];
  rotationY?: number;
}) {
  const leaf = width / 2;
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      {[-leaf / 2, leaf / 2].map((x, i) => (
        <group key={i} position={[x, 0, 0]}>
          {/* marco de aluminio */}
          <mesh position={[0, height / 2, 0]} castShadow>
            <boxGeometry args={[leaf - 0.04, height, 0.05]} />
            <meshStandardMaterial color="#93a0a3" roughness={0.35} metalness={0.55} />
          </mesh>
          {/* paño de vidrio de la hoja */}
          <mesh position={[0, height / 2, 0.031]}>
            <planeGeometry args={[leaf - 0.22, height - 0.3]} />
            <meshStandardMaterial color="#cfe0e6" roughness={0.08} metalness={0.1} transparent opacity={0.45} />
          </mesh>
          {/* manija vertical */}
          <mesh position={[i === 0 ? leaf / 2 - 0.12 : -leaf / 2 + 0.12, 1.05, 0.06]}>
            <cylinderGeometry args={[0.02, 0.02, 0.7, 8]} />
            <meshStandardMaterial color="#5a6064" metalness={0.7} roughness={0.3} />
          </mesh>
        </group>
      ))}
    </group>
  );
}
